import {
  isRejected,
  isRejectedWithValue,
  Middleware,
  miniSerializeError,
} from '@reduxjs/toolkit';
import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery as rtkBaseQuery,
  FetchBaseQueryError,
  RootState as RtkRootState,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import {
  POSTGREST_BASE_URL,
  PostgrestErrors,
  refreshAccessTokenRequest,
} from '@/requests';
import { growthbook, log, logBreadcrumb } from '@/services';

import {
  clearTokens,
  selectAccessToken,
  selectRefreshToken,
  setTokens,
} from './session';
import { RootState } from './store';

const mutex = new Mutex();

const createBaseQueryWithAuthCredentials: ({
  baseUrl,
}: {
  baseUrl: string;
}) => BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> =
  ({ baseUrl }) =>
  (args, redux, extraOptions) => {
    const token = selectAccessToken(redux.getState() as RootState);

    return rtkBaseQuery({
      baseUrl,
      credentials: 'same-origin', // TODO
      prepareHeaders: headers => {
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      },
    })(args, redux, extraOptions);
  };

const createBaseQueryWithRefresh =
  ({ baseUrl }: { baseUrl: string }) =>
  async (args: string | FetchArgs, redux: BaseQueryApi, extraOptions: {}) => {
    const baseQuery = createBaseQueryWithAuthCredentials({ baseUrl });

    await mutex.waitForUnlock();
    const result = await baseQuery(args, redux, extraOptions);
    const isAuthError = result.error?.status === 401;

    if (!isAuthError) return result;
    if (isAuthError && mutex.isLocked()) {
      await mutex.waitForUnlock();
      return baseQuery(args, redux, extraOptions);
    }
    if (isAuthError && !mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshToken = selectRefreshToken(redux.getState() as RootState);
        if (!refreshToken) {
          await redux.dispatch(clearTokens());
          return result;
        }

        const { data, error } = await rtkBaseQuery({
          baseUrl: POSTGREST_BASE_URL, // always POSTGREST for both apis
        })(refreshAccessTokenRequest({ refreshToken }), redux, extraOptions);

        if (error) throw error;

        await redux.dispatch(
          setTokens({ accessToken: (data as any).access_token, refreshToken }),
        );

        return baseQuery(args, redux, extraOptions);
      } catch (error) {
        log(new Error('Error refreshing token'), error);
        await redux.dispatch(clearTokens());
        window.location.reload();
      } finally {
        release();
      }
    }
    return result;
  };

export const createBaseQueryWithLock =
  ({ baseUrl }: { baseUrl: string }) =>
  async (
    args: string | FetchArgs,
    redux: BaseQueryApi,
    extraOptions: {
      withLock?: boolean;
    },
  ) => {
    const baseQuery = createBaseQueryWithRefresh({ baseUrl });

    const request = async () => {
      const result = await baseQuery(args, redux, extraOptions);
      if (result.error) {
        logRtkQueryError(result.error);
      }
      return result;
    };

    if (extraOptions?.withLock) {
      const lockName = JSON.stringify(args);
      return navigator.locks.request(lockName, request);
    }

    return request();
  };

export const errorLogger: Middleware<
  {},
  RtkRootState<{}, string, 'postgrestApi'>
> = () => next => action => {
  logRtkQueryError(action);
  logUnhandledThunkError(action);

  return next(action);
};

export const rtkQueryError: (error: unknown) => {
  error: FetchBaseQueryError;
} = error => ({
  error: {
    status: 'CUSTOM_ERROR',
    data: miniSerializeError(error),
    error: 'UNHANDLED_ERROR',
  },
});

export const handleOnQueryStartedError = (error: any) => {
  if (error.isUnhandledError !== false) throw error;
};

const isRTKQueryError = (action: any) => {
  return (
    isRejected(action) &&
    ['executeQuery', 'executeMutation'].some(type =>
      action.type?.includes(type),
    )
  );
};

const isUnhandledThunkError = (action: any) => {
  return (
    isRejected(action) &&
    !isRejectedWithValue(action) &&
    !['executeQuery', 'executeMutation'].some(type =>
      action.type?.includes(type),
    )
  );
};

export const parseErrorMessageFromActionPayload = (payload: any) => {
  return (
    payload?.data?.details ||
    payload?.data?.message ||
    payload?.data?.error?.message ||
    payload?.data?.error?.details ||
    payload?.data?.error ||
    payload?.error?.message
  );
};

const getErrorMessageFromAction = (action: any) => {
  const message =
    parseErrorMessageFromActionPayload(action.payload) || action.error?.message;

  if (typeof message === 'string') return message;
  else {
    logBreadcrumb({
      message: 'Parsed error message was not a string',
      data: { message },
    });
    return 'Unknown';
  }
};

const getStringifiedInfo = ({
  error,
  payload,
}: {
  error: any;
  payload: any;
}) => {
  try {
    return {
      stringifiedError: JSON.stringify(error),
      stringifiedPayload: JSON.stringify(payload),
    };
  } catch {
    log(new Error('Error stringifying error or payload'), {
      message: 'Error stringifying error or payload',
      data: { error, payload },
    });
    return {
      stringifiedError: 'Error stringifying error',
      stringifiedPayload: 'Error stringifying payload',
    };
  }
};
const logRtkQueryError = (action: any) => {
  if (isRTKQueryError(action)) {
    if (
      action.error?.name === 'ConditionError' ||
      action.payload?.status === 'FETCH_ERROR' ||
      action.payload?.status === 'PARSING_ERROR' ||
      Object.values(PostgrestErrors.Onboarding).includes(
        action.payload?.data?.details,
      )
    )
      return;

    const endpoint = action.meta.arg?.endpointName;
    const { stringifiedError, stringifiedPayload } = getStringifiedInfo({
      error: action.error,
      payload: action.payload,
    });

    log(
      new Error(
        `${action.type?.replace('rejected', endpoint)}: ${getErrorMessageFromAction(action)}`,
      ),
      {
        type: action.type,
        endpoint,
        args: action.meta.arg?.originalArgs,
        payload: action.payload,
        error: action.error,
        stringifiedError,
        stringifiedPayload,
      },
    );
  }
};

const logUnhandledThunkError = (action: any) => {
  if (isUnhandledThunkError(action)) {
    log(new Error(`${action.type}: ${getErrorMessageFromAction(action)}`), {
      type: action.type,
      args: action.meta?.arg,
      error: action.error,
      payload: action.payload,
    });
  }
};

export const transformAuthErrorResponse = (
  error: any,
  errorCodes: Record<string, string>,
) => {
  const { status, data } = error;
  if (status === 400) {
    const code = (data as { code: string }).code;
    const errorMessage = errorCodes[code];
    if (errorMessage) {
      return {
        status,
        data: {
          message: errorMessage,
        },
      };
    }
  }
  return {
    status,
    data: {
      message: DEFAULT_ERROR_MESSAGE,
    },
  };
};

export const transformErrorResponse = (
  error: any,
  errorCodes: Record<string, string>,
) => {
  const { status, data } = error;
  if (status === 400) {
    const details = (data as { details: string }).details;
    const errorMessage = errorCodes[details];
    if (errorMessage) {
      return {
        status,
        data: {
          message: errorMessage,
        },
      };
    }
  }
  return {
    status,
    data: {
      message: DEFAULT_ERROR_MESSAGE,
    },
  };
};

const DEFAULT_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact support.';

export const useLegacyCDNHost = () => {
  const gbFlag = growthbook.getFeatureValue('image-cdn-update', false);
  const cdnFlag = new URLSearchParams(window.location.search).get('cdn2');

  // If url flag is present, it will supercede growthbook setting
  return cdnFlag ? cdnFlag !== 'true' : !gbFlag;
};

export const swapCDNHost = (url: string, filter: string = ''): string => {
  if (!url || useLegacyCDNHost()) {
    return url;
  }

  const obj = new URL(url);

  // Update the host and pathname to use the new CDN format
  obj.host = 'cdn.lumiqlearn.com';
  obj.pathname = `${filter}${decodeURIComponent(obj.pathname)}`;
  return obj.toString();
};

export const swapCDNImageHost = (url: string, fallback?: boolean): string => {
  return swapCDNHost(
    url,
    `cdn-cgi/image/format=auto${fallback ? ',onerror=redirect' : ''}`,
  );
};
